import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Button,
  TextField,
  DialogActions,
} from "@mui/material";
import CustomTypography from "../../../Components/Typography";
import { actionButtonStyle, removeActionBtn } from "../styles/styles";
import { useTranslation } from "react-i18next";

const RemoveSupplierModal = (props) => {
  const {
    viewType,
    supplier,
    openRemoveSupplier,
    setOpenRemoveSupplier,
    onRemove,
    openOrdersCount = 0,
    isPaused = false,
    onPauseInstead,
    pausingSupplier = false,
  } = props;
  const [reason, setReason] = useState("");

  // Open orders don't block removal — the supplier keeps access to them and
  // completes them as normal, exactly as when a supplier leaves a community on
  // their own. They only change what the modal says: the CM is told what will
  // happen to those orders, and offered the pause in case stopping new orders
  // is all they meant to do.
  const hasOpenOrders = openOrdersCount > 0;

  const { t } = useTranslation("remove-supplier-modal");

  const onClose = () => {
    setOpenRemoveSupplier(false);
    setReason("");
  };

  const handleRemove = () => {
    if (reason.trim().length > 0) {
      onRemove(reason);
      onClose();
    }
  };

  return (
    <Dialog
      open={openRemoveSupplier}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: "24px", py: "18px", borderBottom: "1px solid #EAEAEA" }}
      >
        <CustomTypography
          type="subheading"
          props={{
            color: "#2F2B3DE5",
            lineHeight: "28px",
          }}
        >
          {viewType === "offer" ? t("decline_offer") : t("remove_supplier")}
        </CustomTypography>

        <IconButton onClick={onClose} sx={{ padding: "6px" }}>
          <img src="/cancel_icon.svg" alt="close" width={14} height={14} />
        </IconButton>
      </Box>

      <DialogContent sx={{ py: "32px", px: "24px" }}>
        <Box sx={{ maxWidth: "508px", width: { xs: "100%", sm: "508px" } }}>
          {hasOpenOrders && (
            <Box sx={{ mb: "24px" }}>
              <CustomTypography
                type="label"
                props={{ color: "#2F2B3DE5", mb: "12px" }}
              >
                {openOrdersCount === 1
                  ? t("open_orders_title_single", { name: supplier.name })
                  : t("open_orders_title_plural", {
                      name: supplier.name,
                      count: openOrdersCount,
                    })}
              </CustomTypography>

              <CustomTypography
                props={{
                  color: "#2F2B3DB2",
                  fontSize: "15px",
                  fontWeight: 400,
                  mb: "12px",
                }}
              >
                {t("open_orders_explanation")}
              </CustomTypography>

              <CustomTypography
                props={{
                  color: "#2F2B3DB2",
                  fontSize: "15px",
                  fontWeight: 400,
                }}
              >
                {isPaused
                  ? t("open_orders_already_paused")
                  : t("open_orders_pause_hint")}
              </CustomTypography>
            </Box>
          )}

          <CustomTypography
            type="label"
            props={{
              color: "#2F2B3DE5",
              mb: "12px",
            }}
          >
            {t("why_you_want")}{" "}
            {viewType === "offer" ? t("decline") : t("remove")} {supplier.name}?
          </CustomTypography>

          <TextField
            multiline
            fullWidth
            minRows={4}
            placeholder={t("write_reason")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              color: "#3E3F4CB2",
              background: "transparent",

              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                px: "12px",
                py: "8px",
                fontWeight: 400,
                fontSize: "15px",
                "& fieldset": {
                  borderColor: "#E6E4EB",
                },

                "&:hover fieldset": {
                  borderColor: "#46a091",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#46a091",
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          py: "12px",
          px: "24px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            ...actionButtonStyle,
            background: "#808390",
            "&:hover": { backgroundColor: "#6E707C" },
          }}
        >
          {t("back_button")}
        </Button>

        <Box display="flex" gap="12px">
          {/* Pause is the softer option, offered only while something is still
              running and the supplier isn't paused already. */}
          {hasOpenOrders && !isPaused && (
            <Button
              onClick={onPauseInstead}
              variant="contained"
              disabled={pausingSupplier}
              sx={actionButtonStyle}
            >
              {t("pause_supplier_button")}
            </Button>
          )}

          <Button
            onClick={handleRemove}
            variant="contained"
            disabled={reason.trim().length === 0}
            sx={removeActionBtn}
          >
            {viewType === "offer"
              ? t("ok_button.decline")
              : t("ok_button.remove")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveSupplierModal;
